import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useHttpClient } from "../../hooks/useHttpClient";
import type {
  ClassData,
  UpcomingSession,
} from "../../interfaces/classSessions";
import { useLocalization } from "../../stores/localizationContext";
import { useUserContext } from "../../stores/userContext";
import { formatSessionTimeRange } from "../../utils";
import Card from "../Common/Card/Card";
import List from "../Common/List/List";
import "./Home.scss";
import { useLiveSessionStore } from "../../stores/useLiveSessionStore";

export const Home: React.FC = () => {
  const { user } = useUserContext();
  const api = useHttpClient();
  const { t, lang } = useLocalization();
  const navigate = useNavigate();
  const [upcomingSessions, setUpcomingSessions] = useState<UpcomingSession[]>(
    []
  );
  const [classes, setClasses] = useState<ClassData[]>([]);
  const setSessionDurationInMinutes = useLiveSessionStore((state) => state.setSessionDurationInMinutes);

  useEffect(() => {
    const fetchClasses = async () => {
      if (!user?.userId) return;

      try {
        const endpoint = import.meta.env.VITE_GET_EDUCATOR_CLASSES.replace(
          "{educator-id}",
          user.userId
        );
        const response = await api.get(endpoint);
        console.log(response.data);

        // Store classes data from API response
        if (response.data?.classes) {
          setClasses(response.data.classes);
        }

        // Store upcoming sessions from API response
        if (response.data?.upcoming_sessions) {
          setUpcomingSessions(response.data.upcoming_sessions);
        }
      } catch (err: any) {
        console.error("Error fetching classes:", err);
      }
    };

    fetchClasses();
  }, [user?.userId, api]);

  return (
    <>
      <div className="home-page">
        <div className="sessions">
          <div className="container">
            <div className="section-title">
              {t("upcoming_sessions_label")}
            </div>
          </div>
          <div className="slider">
            <div className="slider__container">
              <div className="cards">
                {upcomingSessions.length > 0 ? (
                  upcomingSessions.map((session) => {
                    const locale = lang === "ar" ? "ar-EG" : "en-US";
                    const date = formatSessionTimeRange(
                      session.session_start_date,
                      session.session_end_date,
                      locale
                    );
                    const bufferMinutes = Number(
                      import.meta.env.VITE_BUFFER_START_SESSION ?? 0
                    );
                    const isInProgress = session.status === "In Progress";
                    const canStart =
                      isInProgress ||
                      new Date(session.current_server_date).getTime() +
                        bufferMinutes * 60 * 1000 >=
                        new Date(session.session_start_date).getTime();
                    return (
                      <Card
                        key={session.session_id}
                        type="session"
                        title={session.session_title}
                        subject={session.class_title}
                        date={date}
                        duration={session.session_duration_in_minutes}
                        status={session.status}
                        statusId={session.status_id}
                        classId={session.class_id}
                        classes={classes}
                        canStart={canStart}
                        onPreview={() =>
                          console.log(
                            `Preview clicked for session ${session.session_id}`
                          )
                        }
                        onViewSession={() =>
                          navigate(
                            `/${lang}/classes/${session.class_id}/sessions/${session.session_id}`
                          )
                        }
                        onStart={() => {
                          if (
                            session.materials &&
                            session.materials.length > 0
                          ) {
                            // Use the first material ID from the materials array
                            const materialId = session.materials[0];
                            const materialIdStr = materialId.toString();
                            setSessionDurationInMinutes(session.session_duration_in_minutes);
                            // Store MD5 hash in sessionStorage for later use by LiveSession
                            if (
                              session.materials_md5 &&
                              session.materials_md5[materialId]
                            ) {
                              const md5Hash = session.materials_md5[materialId];
                              // Store in sessionStorage for LiveSession to use
                              sessionStorage.setItem(
                                `session_${session.session_id}_material_md5`,
                                md5Hash
                              );

                            } else {
                              console.warn(
                                `No MD5 found for material ${materialId}`
                              );
                            }

                            navigate(
                              `/${lang}/sessions/${session.session_id}/live/${materialIdStr}`
                            );
                          } else {
                            // If no materials array or it's empty, use a default material ID
                            console.warn(
                              "No materials provided for session, using default"
                            );
                            navigate(
                              `/${lang}/sessions/${session.session_id}/live/default-material`
                            );
                          }
                        }}
                      />
                    );
                  })
                ) : (
                  <div className="no-sessions">
                    <p>
                      {t(
                        "no_upcoming_sessions",
                        "No upcoming sessions available"
                      )}
                    </p>
                  </div>
                )}
              </div>
              <div className="slider__arrows">
                <button
                  className="scrollbar-arrow scrollbar-arrow--left btn btn-link"
                  data-target="scrollbar-screens"
                >
                  <i className="f-icon chevron-left-icon"></i>
                </button>
                <button
                  className="scrollbar-arrow scrollbar-arrow--right btn btn-link"
                  data-target="scrollbar-screens"
                >
                  <i className="f-icon chevron-right-icon"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="list list-classes">
          <div className="container">
            <div className="section-title">
              <h2>{t("my_classes_label")}</h2>
            </div>
            <div className="list__container">
              <List classes={classes} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
